import { useEffect, useMemo, useState } from "react";
import { Legislation } from "@/models/legislation/Legislation";
import DOMPurify from "dompurify";
import { rewriteRelativeMediaUrls } from "@/pages/legislation/utils/htmlUrlUtils";
import {
  DataSource,
  getDisplayName,
  getOriginUrl,
  normalizeDataSource,
  ORIGIN_BY_SOURCE,
} from "@/config/datasources";
import { logger } from "@/lib/logger";

const containerSasUrl = import.meta.env.VITE_CSS_CONTAINER_SAS_URL as
  | string
  | undefined;

interface UseLegislationContentOptions {
  legislation: Legislation | null;
  datasource: string | undefined;
}

interface UseLegislationContentResult {
  rawCSS: string;
  rawHTML: string;
  loading: boolean;
  error: string | null;
}

/**
 * HTML processors for each datasource type.
 * Returns processed HTML string.
 */
type HtmlProcessor = (
  safe: string,
  datasource: string | undefined
) => string;

/**
 * Remove everything from the start of `root` up to and including the top-level
 * block that contains the "Den fulde tekst" marker (inside div.bjelke).
 * No-op when the marker is absent.
 */
const stripRetsinformationMastheadBeforeFuldeTekst = (
  root: HTMLElement
): void => {
  const bjelkeNodes = Array.from(root.querySelectorAll("div.bjelke"));
  const marker = bjelkeNodes.find(
    (el) => (el.textContent || "").trim() === "Den fulde tekst"
  );
  if (!marker) return;

  let top: Node & ParentNode = marker as unknown as Node & ParentNode;
  while (top.parentNode && top.parentNode !== root) {
    top = top.parentNode as Node & ParentNode;
  }

  let node: Node | null = root.firstChild;
  while (node) {
    const next = node.nextSibling;
    root.removeChild(node);
    if (node === top) break;
    node = next;
  }
};

const hasClassToken = (el: Element, token: string): boolean => {
  const classes = el.className?.toString() ?? "";
  return classes.split(/\s+/).some((cls) => cls.includes(token));
};

const isIndholdsfortegnelseTable = (table: HTMLTableElement): boolean => {
  if (
    hasClassToken(table, "Indholdsfortegnelse") ||
    hasClassToken(table, "Containertable")
  ) {
    return true;
  }

  return Boolean(
    table.querySelector(
      "td[class*='IndholdsfortegnelseId'], td[class*='IndholdsfortegnelseTekst']"
    )
  );
};

/**
 * Remove Retsinformation masthead blocks from container-wrapped consolidation
 * HTML without discarding preamble content (Titel2, Indledning2). Strips
 * `div.bjelke` banners and linked/publisher TOC markup
 * (`table`/`p` Indholdsfortegnelse). Silver HTML uses
 * `stripRetsinformationMastheadBeforeFuldeTekst` instead.
 */
const stripConsolidatedMasthead = (root: HTMLElement): void => {
  root.querySelectorAll("div.bjelke").forEach((el) => {
    el.parentNode?.removeChild(el);
  });

  root.querySelectorAll("table").forEach((table) => {
    if (!isIndholdsfortegnelseTable(table)) return;
    table.parentNode?.removeChild(table);
  });

  root.querySelectorAll("p").forEach((paragraph) => {
    if (!hasClassToken(paragraph, "Indholdsfortegnelse")) return;
    paragraph.parentNode?.removeChild(paragraph);
  });

  root.querySelectorAll("[data-container-type]").forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.textContent?.trim()) return;
    el.parentNode?.removeChild(el);
  });
};

const processRetsinformationHtml: HtmlProcessor = (safe, datasource) => {
  const div = document.createElement("div");
  div.innerHTML = safe;

  stripRetsinformationMastheadBeforeFuldeTekst(div);

  // Wrap remaining content into the expected structure
  const restylingRoot = document.createElement("div");
  restylingRoot.id = "restylingRoot";
  restylingRoot.className = "document-content";
  // Move all children of div into restylingRoot
  while (div.firstChild) restylingRoot.appendChild(div.firstChild);

  const col = document.createElement("div");
  col.className = "col";
  col.appendChild(restylingRoot);

  const row = document.createElement("div");
  row.className = "row";
  row.appendChild(col);

  // Rewrite relative media URLs for Retsinformation
  const origin = getOriginUrl(datasource) ?? ORIGIN_BY_SOURCE[datasource || ""];
  return rewriteRelativeMediaUrls(row.innerHTML, origin);
};

/**
 * Strip `<style>` from stored legislation HTML and sanitize the body.
 *
 * The raw markup is parsed into an inert DOMParser document, never into an
 * element of the live page: `innerHTML` on a live element fetches images and
 * runs inline `onerror`/`onload` handlers before DOMPurify has seen the markup.
 * Head content (`<title>`, `<meta>`) is dropped; DOMPurify removed it anyway.
 */
const sanitizeLegislationBody = (rawHtmlString: string): string => {
  const doc = new DOMParser().parseFromString(rawHtmlString, "text/html");
  doc.body.querySelectorAll("style").forEach((el) => el.remove());
  return DOMPurify.sanitize(doc.body.innerHTML, {
    ALLOW_UNKNOWN_PROTOCOLS: false,
    WHOLE_DOCUMENT: false,
  });
};

/**
 * Lighter processor for Consolidation API HTML — containers already carry stable ids.
 */
export const processConsolidatedHtml = (
  rawHtmlString: string,
  datasource: string | undefined
): string => {
  if (!rawHtmlString) return "";

  const safe = sanitizeLegislationBody(rawHtmlString);

  const restylingRoot = document.createElement("div");
  restylingRoot.id = "restylingRoot";
  restylingRoot.className = "document-content";
  restylingRoot.innerHTML = safe;

  stripConsolidatedMasthead(restylingRoot);

  const col = document.createElement("div");
  col.className = "col";
  col.appendChild(restylingRoot);

  const row = document.createElement("div");
  row.className = "row";
  row.appendChild(col);

  const origin = getOriginUrl(datasource) ?? ORIGIN_BY_SOURCE[datasource || ""];
  return rewriteRelativeMediaUrls(row.innerHTML, origin);
};

const processArvidHtml: HtmlProcessor = (safe) => {
  const container = document.createElement("div");
  container.innerHTML = safe;

  const sections: Array<{
    id: string;
    addCard: boolean;
    title: string;
    hasBottomMargin: boolean;
  }> = [
    {
      id: "summary",
      addCard: true,
      title: "Resumé",
      hasBottomMargin: true,
    },
    {
      id: "content",
      addCard: false,
      title: "Dokument",
      hasBottomMargin: true,
    },
    {
      id: "sources",
      addCard: true,
      title: "Retskilder",
      hasBottomMargin: false,
    },
  ];

  sections.forEach(({ id, addCard, title, hasBottomMargin }) => {
    const el = container.querySelector(`div#${id}`) as HTMLDivElement | null;
    if (el) {
      if (addCard) el.classList.add("card-body", "bg-muted", "dark:bg-secondary");
      if (hasBottomMargin) el.classList.add("mb-4");
      const h4 = document.createElement("h4");
      h4.textContent = title;
      el.insertBefore(h4, el.firstChild);
    }
  });

  return container.innerHTML;
};

const processEurlexHtml: HtmlProcessor = (safe) => {
  const container = document.createElement("div");
  container.innerHTML = safe;

  // Remove any <head> elements
  const heads = Array.from(container.querySelectorAll("head"));
  heads.forEach((h) => h.parentNode?.removeChild(h));

  // Remove banners
  const banners = Array.from(container.querySelectorAll("div#banner"));
  banners.forEach((b) => b.parentNode?.removeChild(b));

  // Determine where the real content lives
  const body = container.querySelector("body");
  const rootEl = (body as HTMLElement) || container;

  // Determine language from original markup if present
  const htmlLang = container.querySelector("html")?.getAttribute("lang");
  const rootLang = (rootEl as HTMLElement).getAttribute("lang");
  const langAttr = htmlLang || rootLang || "";

  // Build required wrapper structure
  const textDiv = document.createElement("div");
  textDiv.id = "text";

  const textTabContentDiv = document.createElement("div");
  textTabContentDiv.id = "textTabContent";
  textTabContentDiv.className = "tabContent";

  const tabContentDiv = document.createElement("div");
  tabContentDiv.id = "tabContent";

  const langDiv = document.createElement("div");
  if (langAttr) langDiv.setAttribute("lang", langAttr);

  // Move all child nodes from rootEl into the innermost langDiv
  while (rootEl.firstChild) {
    langDiv.appendChild(rootEl.firstChild);
  }

  tabContentDiv.appendChild(langDiv);
  textTabContentDiv.appendChild(tabContentDiv);
  textDiv.appendChild(textTabContentDiv);

  return textDiv.outerHTML;
};

/**
 * Domsdatabasen HTML processor.
 * Removes fixed pixel widths and margins to make content responsive.
 */
const processDomsdatabasenHtml: HtmlProcessor = (safe) => {
  const container = document.createElement("div");
  container.innerHTML = safe;

  // Remove fixed width from .page elements (inline styles)
  container.querySelectorAll(".page").forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.width = "100%";
    htmlEl.style.maxWidth = "1024px";
    htmlEl.style.minHeight = "auto";
  });

  // Remove fixed margins from blocks that cause overflow and large left spacing
  container
    .querySelectorAll(".ddb-block, .ddb-block-nb, .text")
    .forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.marginRight = "0";
      htmlEl.style.marginLeft = "0";
    });

  // Make tables responsive
  container.querySelectorAll(".ddb-table").forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.width = "100%";
    htmlEl.style.maxWidth = "1024px";
  });

  return container.innerHTML;
};

/**
 * HTML processor registry by DataSource.
 */
const HTML_PROCESSORS: Partial<Record<DataSource, HtmlProcessor>> = {
  [DataSource.Domsdatabasen]: processDomsdatabasenHtml,
  [DataSource.Retsinformation]: processRetsinformationHtml,
  [DataSource.Arvid]: processArvidHtml,
  [DataSource.Eurlex]: processEurlexHtml,
};

/**
 * Processes raw legislation HTML for a given datasource.
 * Strips <style> and <head> tags, sanitizes with DOMPurify, and applies
 * any datasource-specific DOM transformations.
 *
 * Exported for use in comparison utilities that need to process HTML outside
 * the main useLegislationContent hook (e.g. useLegislationComparison).
 */
export const processLegislationHtml = (
  rawHtmlString: string,
  datasource: string | undefined
): string => {
  if (!rawHtmlString) return "";

  const normalizedDataSource = normalizeDataSource(datasource);

  const safe = sanitizeLegislationBody(rawHtmlString);

  if (normalizedDataSource) {
    const processor = HTML_PROCESSORS[normalizedDataSource];
    if (processor) {
      return processor(safe, datasource);
    }
  }

  return safe;
};

/**
 * Custom hook that handles all legislation content processing:
 * - Extracts inline CSS from HTML
 * - Processes HTML based on datasource
 * - Fetches external CSS files
 */
export const useLegislationContent = ({
  legislation,
  datasource,
}: UseLegislationContentOptions): UseLegislationContentResult => {
  const [rawCSS, setRawCSS] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Split base and query from the SAS url
  const { baseUrl, sasQuery } = useMemo(() => {
    if (!containerSasUrl) return { baseUrl: "", sasQuery: "" };
    const qIndex = containerSasUrl.indexOf("?");
    if (qIndex === -1)
      return { baseUrl: containerSasUrl.replace(/\/$/, ""), sasQuery: "" };
    return {
      baseUrl: containerSasUrl.substring(0, qIndex).replace(/\/$/, ""),
      sasQuery: containerSasUrl.substring(qIndex), // includes the leading '?'
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerSasUrl]);

  const extractedInlineCSS = useMemo(() => {
    const htmlString = legislation?.htmlWithIds ?? legislation?.html ?? "";
    if (!htmlString) return "";

    // Inert parse: see sanitizeLegislationBody.
    const doc = new DOMParser().parseFromString(htmlString, "text/html");

    const styleEls = Array.from(doc.querySelectorAll("style"));
    const styles = styleEls
      .map((el) => el.textContent || "")
      .join("\n\n/* --- next style block --- */\n\n");

    return styles;
  }, [legislation?.htmlWithIds, legislation?.html]);

  const rawHTML = useMemo(
    () =>
      processLegislationHtml(
        legislation?.htmlWithIds ?? legislation?.html ?? "",
        datasource
      ),
    [legislation?.htmlWithIds, legislation?.html, datasource]
  );

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      setRawCSS(extractedInlineCSS || "");
      try {
        const mappedDatasource = getDisplayName(datasource);
        if (!mappedDatasource)
          throw new Error("Mangler eller ukendt 'datasource' i URL'en.");
        if (!baseUrl || !sasQuery)
          throw new Error(
            "Mangler VITE_CSS_CONTAINER_SAS_URL i miljøvariabler."
          );

        const listUrl =
          `${baseUrl}?restype=container&comp=list&prefix=${encodeURIComponent(
            mappedDatasource
          )}/` +
          `${
            sasQuery.startsWith("?")
              ? sasQuery.substring(1)
              : sasQuery
                ? sasQuery
                : ""
          }`.replace(/^/, "&");

        const res = await fetch(listUrl);
        if (!res.ok)
          throw new Error(`Blob list fejlede: ${res.status} ${res.statusText}`);
        const xmlText = await res.text();

        // Parse XML
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlText, "application/xml");
        const errorNode = doc.querySelector("ParserError");
        if (errorNode) throw new Error("Kunne ikke parse XML fra blob-listen.");

        const names = Array.from(doc.getElementsByTagName("Name"))
          .map((n) => n.textContent || "")
          .filter((n) => n.toLowerCase().endsWith(".css"));

        const urls = names.map((name) => `${baseUrl}/${name}${sasQuery}`);
        const cssTexts = await Promise.all(
          urls.map(async (u) => {
            const r = await fetch(u);
            if (!r.ok)
              throw new Error(
                `Kunne ikke hente CSS: ${r.status} ${r.statusText}`
              );
            return await r.text();
          })
        );
        const combined = cssTexts.join("\n\n/* --- next file --- */\n\n");
        const finalCss = [combined, extractedInlineCSS]
          .filter(Boolean)
          .join("\n\n/* --- extracted from HTML --- */\n\n");
        if (!cancelled) setRawCSS(finalCss);
      } catch (error) {
        if (cancelled) return;
        logger.error("Failed to load the legislation stylesheets", {
          operation: "fetchLegislationCss",
          error,
          datasource,
        });
        setError(error instanceof Error ? error.message : "Ukendt fejl");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [datasource, baseUrl, sasQuery, extractedInlineCSS]);

  return {
    rawCSS,
    rawHTML,
    loading,
    error,
  };
};
